import { useTranslation } from"react-i18next";
import { useState, useEffect } from"react";
import axios from"axios";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Badge } from"@/components/ui/badge";
import { CheckCircle, Clock, Package, Handshake, ChevronDown, ChevronUp, Building2, FileText, CheckCircle2, XCircle } from"lucide-react";
import { toast } from"sonner";
import { useNavigate } from"react-router-dom";

const EngineerDashboard = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [dashboard, setDashboard] = useState(null);
 const [loading, setLoading] = useState(true);
 const [pendingReports, setPendingReports] = useState([]);
 const [pendingInvoices, setPendingInvoices] = useState([]);
 const [reviewingReport, setReviewingReport] = useState(null);
 const [reviewNotes, setReviewNotes] = useState("");
 const [adjustedMaterialTotal, setAdjustedMaterialTotal] = useState("");
 const [reviewingInvoice, setReviewingInvoice] = useState(null);
 const [invoiceNotes, setInvoiceNotes] = useState("");
 const [expandedSections, setExpandedSections] = useState({
 tasks: true,
 units: false,
 subcontractors: false
 });
 const navigate = useNavigate();

 useEffect(() => {
 loadDashboard();
 }, []);

 const loadDashboard = async () => {
 try {
 const [dashRes, reportsRes, invoicesRes] = await Promise.all([
 axios.get("/dashboard/engineer"),
 axios.get("/progress-reports/pending"),
 axios.get("/contractor-invoices/pending")
 ]);
 setDashboard(dashRes.data);
 setPendingReports(reportsRes.data || []);
 setPendingInvoices(invoicesRes.data || []);
 } catch (error) {
 toast.error(t('messages.loadFailed'));
 } finally {
 setLoading(false);
 }
 };

 const reviewReport = async (reportId, status) => {
 try {
 const payload = { status, notes: reviewNotes };
 if (adjustedMaterialTotal !== "") {
 payload.adjusted_material_total = parseFloat(adjustedMaterialTotal);
 }
 await axios.patch(`/progress-reports/${reportId}/review`, payload);
 toast.success(status === "approved" ? "Report approved — contractor can now invoice" : "Report rejected");
 setReviewingReport(null);
 setReviewNotes("");
 setAdjustedMaterialTotal("");
 setPendingReports(prev => prev.filter(r => r.id !== reportId));
 } catch {
 toast.error("Failed to review report");
 }
 };

 const reviewInvoice = async (invoiceId, action) => {
 try {
 await axios.patch(`/contractor-invoices/${invoiceId}/approve`, { action, notes: invoiceNotes });
 toast.success(action === "approve" ? "Invoice approved — sent to accountant" : "Invoice rejected");
 setReviewingInvoice(null);
 setInvoiceNotes("");
 setPendingInvoices(prev => prev.filter(i => i.id !== invoiceId));
 } catch (err) {
 toast.error(err.response?.data?.detail || "Failed to review invoice");
 }
 };

 const toggleSection = (section) => {
 setExpandedSections(prev => ({
 ...prev,
 [section]: !prev[section]
 }));
 };

 const getStatusColor = (status) => {
 switch (status) {
 case"completed": return"bg-emerald-500/10 text-emerald-700 border-green-200/60";
 case"in_progress": return"bg-blue-500/10 text-blue-700 border-blue-200/60";
 case"not_started": return"bg-white/60 text-[#334155] border-slate-200";
 default: return"bg-white/60 text-[#334155] border-slate-200";
 }
 };

 const getUnitStatusColor = (status) => {
 switch (status) {
 case"available": return"bg-emerald-500/10 text-emerald-700";
 case"sold": return"bg-blue-500/10 text-blue-700";
 case"reserved": return"bg-orange-100 text-orange-700";
 case"rented": return"bg-purple-100 text-purple-700";
 default: return"bg-white/60 text-[#334155]";
 }
 };

 if (loading) {
 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="flex items-center justify-center h-64">
 <div className="text-slate-600">{t('engineer.loadingDashboard')}</div>
 </div>
 </Layout>
 );
 }

 const stats = [
 {
 title: t('engineer.activeTasks'),
 value: dashboard?.active_tasks_count || 0,
 icon: Clock,
 color:"bg-blue-500",
 testId:"active-tasks-stat"
 },
 {
 title: t('dashboard.totalUnits'),
 value: dashboard?.unit_stats?.total || 0,
 icon: Package,
 color:"bg-emerald-500",
 testId:"total-units-stat"
 },
 {
 title: t('nav.subcontractors'),
 value: dashboard?.subcontractors_count || 0,
 icon: Handshake,
 color:"bg-purple-500",
 testId:"subcontractors-stat"
 },
 {
 title: "Pending Reports",
 value: pendingReports.length,
 icon: FileText,
 color: pendingReports.length > 0 ?"bg-amber-500":"bg-slate-400",
 testId:"pending-reports-stat"
 },
 {
 title: "Pending Invoices",
 value: pendingInvoices.length,
 icon: FileText,
 color: pendingInvoices.length > 0 ?"bg-red-500":"bg-slate-400",
 testId:"pending-invoices-stat"
 },
 ];

 return (
 <Layout user={user} onLogout={onLogout}>
 <div data-testid="engineer-dashboard"className="space-y-6">
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900 mb-1">{t('engineer.title')}</h1>
 <p className="text-sm text-slate-500">{t('engineer.subtitle')}</p>
 </div>

 {/* Stats */}
 <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
 {stats.map((stat) => {
 const Icon = stat.icon;
 return (
 <Card key={stat.title} data-testid={stat.testId} className="">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-[11px] font-medium text-slate-500 mb-1">{stat.title}</p>
 <p className="text-2xl font-semibold text-slate-900 tabular-nums">{stat.value}</p>
 </div>
 <Icon className="h-7 w-7 text-indigo-600"/>
 </div>
 </CardContent>
 </Card>
 );
 })}
 </div>

 {/* Quick Actions */}
 <div className="flex gap-3">
 <Button
 className="bg-slate-900 hover:bg-slate-800:bg-zinc-200 text-sm"
 onClick={() => navigate("/project-progress")}
 >
 {t('engineer.viewProjectProgress')}
 </Button>
 <Button
 variant="outline"
 className="text-sm"
 onClick={() => navigate("/subcontractors")}
 >
 {t('engineer.viewSubcontractors')}
 </Button>
 </div>

 {/* Pending Reports — action required */}
 {pendingReports.length > 0 && (
 <Card className="border-amber-200">
 <CardHeader className="pb-2">
 <CardTitle className="flex items-center gap-2 text-amber-800">
 <FileText className="w-4 h-4 text-amber-600"/>
 Pending Reports — Action Required
 <span className="ml-auto bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{pendingReports.length}</span>
 </CardTitle>
 </CardHeader>
 <CardContent className="p-0">
 {pendingReports.map(report => (
 <div key={report.id} className="border-t border-amber-100 px-4 py-3">
 <div className="flex items-start justify-between gap-3">
 <div className="flex-1 min-w-0">
 <p className="font-semibold text-slate-900 text-sm truncate">{report.title}</p>
 <p className="text-xs text-slate-500 mt-0.5">
 <span className="font-medium text-slate-700">{report.partner_name}</span>
 {report.milestone_label && <span className="text-amber-600"> · {report.milestone_label}</span>}
 </p>
 {report.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{report.description}</p>}
 {report.material_total > 0 && (
 <div className="mt-1.5 inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded px-2 py-0.5" data-testid={`report-material-badge-${report.id}`}>
 <span className="text-xs font-semibold text-blue-700">
 {report.material_total} {report.material_unit_of_measure || "m\u00B2"}
 </span>
 <span className="text-[10px] text-blue-500">from Project quantities</span>
 </div>
 )}
 {report.attachments?.length > 0 && (
 <div className="mt-1.5 flex flex-wrap gap-1.5">
 {report.attachments.map((url, i) => (
 <a key={i} href={url} target="_blank" rel="noopener noreferrer"
 className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 bg-indigo-50 border border-indigo-200 rounded px-2 py-0.5"
 data-testid={`report-attachment-${report.id}-${i}`}>
 <FileText className="w-3 h-3" /> Doc {i + 1}
 </a>
 ))}
 </div>
 )}
 </div>
 <div className="flex gap-2 flex-shrink-0">
 {reviewingReport === report.id ? (
 <div className="flex flex-col gap-2 w-56">
 {report.material_total > 0 && (
 <div>
 <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
 Adjust {report.material_unit_of_measure || "m\u00B2"} (owner set: {report.material_total})
 </label>
 <input
 type="number" step="0.01"
 className="border border-blue-200 bg-blue-50 rounded-sm px-2 py-1 text-xs w-full"
 placeholder={String(report.material_total)}
 value={adjustedMaterialTotal}
 onChange={e => setAdjustedMaterialTotal(e.target.value)}
 data-testid={`adjust-m2-${report.id}`}
 />
 {adjustedMaterialTotal && parseFloat(adjustedMaterialTotal) !== report.material_total && (
 <p className="text-[10px] text-amber-600 mt-0.5">Changed from {report.material_total} to {adjustedMaterialTotal}</p>
 )}
 </div>
 )}
 <input className="border border-slate-200 rounded-sm px-2 py-1 text-xs w-full"
 placeholder="Notes (optional)" value={reviewNotes}
 onChange={e => setReviewNotes(e.target.value)} />
 <div className="flex gap-1">
 <button onClick={() => reviewReport(report.id, "approved")}
 data-testid={`approve-report-${report.id}`}
 className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white text-xs rounded-sm px-2 py-1 flex items-center justify-center gap-1">
 <CheckCircle2 className="w-3 h-3"/> Approve
 </button>
 <button onClick={() => reviewReport(report.id, "rejected")}
 data-testid={`reject-report-${report.id}`}
 className="flex-1 bg-red-500 hover:bg-red-600 text-white text-xs rounded-sm px-2 py-1 flex items-center justify-center gap-1">
 <XCircle className="w-3 h-3"/> Reject
 </button>
 </div>
 <button onClick={() => { setReviewingReport(null); setAdjustedMaterialTotal(""); }} className="text-xs text-slate-400 text-center">Cancel</button>
 </div>
 ) : (
 <button onClick={() => { setReviewingReport(report.id); setReviewNotes(""); setAdjustedMaterialTotal(""); }}
 data-testid={`review-report-btn-${report.id}`}
 className="text-xs bg-amber-500 hover:bg-amber-600 text-white rounded-sm px-3 py-1.5 font-medium">
 Review
 </button>
 )}
 </div>
 </div>
 </div>
 ))}
 </CardContent>
 </Card>
 )}

 {/* Pending Invoices — action required */}
 {pendingInvoices.length > 0 && (
 <Card className="border-red-200">
 <CardHeader className="pb-2">
 <CardTitle className="flex items-center gap-2 text-red-800">
 <FileText className="w-4 h-4 text-red-600"/>
 Pending Contractor Invoices — Approve or Reject
 <span className="ml-auto bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{pendingInvoices.length}</span>
 </CardTitle>
 </CardHeader>
 <CardContent className="p-0">
 {pendingInvoices.map(inv => (
 <div key={inv.id} className="border-t border-red-100 px-4 py-3">
 <div className="flex items-start justify-between gap-3">
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2">
 <p className="font-semibold text-slate-900 text-sm">{inv.invoice_number}</p>
 <span className="text-xs text-amber-600 font-medium">{inv.partner_name}</span>
 </div>
 {inv.linked_report_title && <p className="text-xs text-slate-500 mt-0.5">Report: {inv.linked_report_title}</p>}
 <div className="flex gap-4 mt-1 text-xs text-slate-500">
 <span className="font-bold text-slate-800">€{inv.total_amount?.toLocaleString()}</span>
 {inv.m2_completed && <span>{inv.m2_completed} {inv.material_unit_of_measure || "m\u00B2"}</span>}
 {inv.report_material_total > 0 && (
 <span className="text-blue-600">verified: {inv.report_material_total} {inv.material_unit_of_measure || "m\u00B2"}</span>
 )}
 {inv.due_date && <span>Due: {new Date(inv.due_date).toLocaleDateString()}</span>}
 </div>
 {inv.invoice_attachments?.length > 0 && (
 <a href={inv.invoice_attachments[0]} target="_blank" rel="noopener noreferrer"
 className="text-xs text-indigo-600 hover:underline mt-1 block">View Invoice Document</a>
 )}
 </div>
 <div className="flex-shrink-0">
 {reviewingInvoice === inv.id ? (
 <div className="flex flex-col gap-2 w-44">
 <input className="border border-slate-200 rounded-sm px-2 py-1 text-xs w-full"
 placeholder="Notes (optional)" value={invoiceNotes}
 onChange={e => setInvoiceNotes(e.target.value)} />
 <div className="flex gap-1">
 <button onClick={() => reviewInvoice(inv.id, "approve")}
 className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white text-xs rounded-sm px-2 py-1 flex items-center justify-center gap-1">
 <CheckCircle2 className="w-3 h-3"/> Approve
 </button>
 <button onClick={() => reviewInvoice(inv.id, "reject")}
 className="flex-1 bg-red-500 hover:bg-red-600 text-white text-xs rounded-sm px-2 py-1 flex items-center justify-center gap-1">
 <XCircle className="w-3 h-3"/> Reject
 </button>
 </div>
 <button onClick={() => setReviewingInvoice(null)} className="text-xs text-slate-400 text-center">Cancel</button>
 </div>
 ) : (
 <button onClick={() => { setReviewingInvoice(inv.id); setInvoiceNotes(""); }}
 className="text-xs bg-red-500 hover:bg-red-600 text-white rounded-sm px-3 py-1.5 font-medium">
 Review
 </button>
 )}
 </div>
 </div>
 </div>
 ))}
 </CardContent>
 </Card>
 )}

 {/* Assigned Projects */}
 {dashboard?.assigned_projects && dashboard.assigned_projects.length > 0 && (
 <Card className="overflow-hidden">
 <div className="px-5 py-4 border-b border-white/40">
 <div className="flex items-center gap-2">
 <CheckCircle className="w-4 h-4 text-emerald-600"/>
 <span className="text-sm font-medium text-[#334155]">{t('engineer.myAssignedProjects')}</span>
 </div>
 </div>
 <div className="p-4">
 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
 {dashboard.assigned_projects.map((project) => (
 <div
 key={project.id}
 className="p-4 bg-white/50 rounded-sm border border-slate-200"
 >
 <p className="text-sm font-medium text-slate-900">{project.name}</p>
 <p className="text-[11px] text-slate-500 mt-1">{t('engineer.code')}: {project.code}</p>
 <p className="text-[11px] text-slate-500">{t('engineer.city')}: {project.city}</p>
 <Badge variant="outline"className="mt-2 bg-emerald-500/10 text-emerald-700 border-emerald-200/60 text-xs">
 {project.status}
 </Badge>
 </div>
 ))}
 </div>
 </div>
 </Card>
 )}

 {/* Active Tasks from Project Progress */}
 <Card className="">
 <CardHeader>
 <button
 onClick={() => toggleSection('tasks')}
 className="w-full flex items-center justify-between"
 >
 <CardTitle className="flex items-center gap-2">
 <Clock className="w-5 h-5 text-blue-600"/>
 {t('engineer.activeTasks')} ({dashboard?.active_tasks_count || 0})
 </CardTitle>
 {expandedSections.tasks ? (
 <ChevronUp className="w-5 h-5 text-slate-400"/>
 ) : (
 <ChevronDown className="w-5 h-5 text-slate-400"/>
 )}
 </button>
 </CardHeader>
 {expandedSections.tasks && (
 <CardContent>
 {dashboard?.active_tasks && dashboard.active_tasks.length > 0 ? (
 <div className="space-y-2">
 {dashboard.active_tasks.map((task) => (
 <div
 key={task.id}
 className="flex items-center justify-between p-3 bg-white/50 rounded-sm border border-slate-200"
 >
 <div className="flex-1">
 <p className="font-medium text-slate-900">{task.job_name}</p>
 <p className="text-[11px] text-slate-500">Phase {task.phase} • {task.trade}</p>
 </div>
 <Badge variant="outline"className={getStatusColor(task.status)}>
 {task.status?.replace("_","")}
 </Badge>
 </div>
 ))}
 </div>
 ) : (
 <p className="text-sm text-slate-500 text-center py-4">{t('engineer.noActiveTasks')}</p>
 )}
 </CardContent>
 )}
 </Card>

 {/* Units Overview */}
 <Card className="">
 <CardHeader>
 <button
 onClick={() => toggleSection('units')}
 className="w-full flex items-center justify-between"
 >
 <CardTitle className="flex items-center gap-2">
 <Building2 className="w-5 h-5 text-emerald-600"/>
 {t('units.title')} ({dashboard?.unit_stats?.total || 0})
 <div className="flex gap-2 ml-4">
 <Badge variant="outline"className="bg-green-50/80 text-green-700 text-xs">
 {dashboard?.unit_stats?.available || 0} {t('units.available')}
 </Badge>
 <Badge variant="outline"className="bg-blue-500/10 text-blue-700 text-xs">
 {dashboard?.unit_stats?.sold || 0} {t('units.sold')}
 </Badge>
 <Badge variant="outline"className="bg-purple-50/80 text-purple-700 text-xs">
 {dashboard?.unit_stats?.rented || 0} {t('units.rented')}
 </Badge>
 </div>
 </CardTitle>
 {expandedSections.units ? (
 <ChevronUp className="w-5 h-5 text-slate-400"/>
 ) : (
 <ChevronDown className="w-5 h-5 text-slate-400"/>
 )}
 </button>
 </CardHeader>
 {expandedSections.units && (
 <CardContent>
 {dashboard?.units && dashboard.units.length > 0 ? (
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
 {dashboard.units.map((unit) => (
 <div
 key={unit.id}
 className="p-3 bg-white/50 rounded-sm border border-slate-200"
 >
 <div className="flex items-center justify-between">
 <p className="font-medium text-slate-900">{unit.code}</p>
 <Badge variant="outline"className={getUnitStatusColor(unit.status)}>
 {unit.status}
 </Badge>
 </div>
 <p className="text-[11px] text-slate-500 mt-1">
 {unit.type} • {unit.area}m² • {unit.bedrooms || 0} {t('engineer.bed')}
 </p>
 <p className="text-sm font-medium text-[#334155] mt-1">
 €{unit.price?.toLocaleString() || 0}
 </p>
 </div>
 ))}
 </div>
 ) : (
 <p className="text-sm text-slate-500 text-center py-4">{t('engineer.noUnitsFound')}</p>
 )}
 </CardContent>
 )}
 </Card>

 {/* Subcontractors Overview */}
 <Card className="">
 <CardHeader>
 <button
 onClick={() => toggleSection('subcontractors')}
 className="w-full flex items-center justify-between"
 >
 <CardTitle className="flex items-center gap-2">
 <Handshake className="w-5 h-5 text-purple-500"/>
 {t('nav.subcontractors')} ({dashboard?.subcontractors_count || 0})
 </CardTitle>
 {expandedSections.subcontractors ? (
 <ChevronUp className="w-5 h-5 text-slate-400"/>
 ) : (
 <ChevronDown className="w-5 h-5 text-slate-400"/>
 )}
 </button>
 </CardHeader>
 {expandedSections.subcontractors && (
 <CardContent>
 {dashboard?.subcontractors && dashboard.subcontractors.length > 0 ? (
 <div className="space-y-2">
 {dashboard.subcontractors.map((sub) => (
 <div
 key={sub.id}
 className="flex items-center justify-between p-3 bg-white/50 rounded-sm border border-slate-200"
 >
 <div className="flex-1">
 <p className="font-medium text-slate-900">{sub.partner_name ||"Unknown"}</p>
 <p className="text-[11px] text-slate-500">{sub.service_type}</p>
 </div>
 <div className="text-right">
 <p className="text-sm font-medium text-[#334155]">
 €{sub.contract_value?.toLocaleString() || 0}
 </p>
 <Badge variant="outline"className="text-xs">
 {sub.status ||"active"}
 </Badge>
 </div>
 </div>
 ))}
 </div>
 ) : (
 <p className="text-sm text-slate-500 text-center py-4">{t('engineer.noSubcontractorsAssigned')}</p>
 )}
 </CardContent>
 )}
 </Card>
 </div>
 </Layout>
 );
};

export default EngineerDashboard;
